import React, { useEffect, useMemo, useState } from 'react';
import { useCompany } from './CompanyContext';
import { reportingAPI } from '../services/api';
import { TimeSeriesPoint } from '../types';
import { useTranslation } from 'react-i18next';

const LineChart: React.FC<{ title: string; series: TimeSeriesPoint[]; color?: string }>
  = ({ title, series, color = '#2563eb' }) => {
  const width = 600;
  const height = 220;
  const padding = 32;
  const points = series;
  const values = points.map(p => p.value);
  const max = Math.max(1, ...values);
  const min = Math.min(0, ...values);
  const xStep = points.length > 1 ? (width - 2 * padding) / (points.length - 1) : 0;
  const yScale = (v: number) => height - padding - ((v - min) / (max - min || 1)) * (height - 2 * padding);
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${padding + i * xStep} ${yScale(p.value)}`).join(' ');

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-soft p-4">
      <div className="text-sm font-medium text-gray-700 mb-2">{title}</div>
      <svg width={width} height={height} className="w-full h-auto">
        <rect x={0} y={0} width={width} height={height} fill="#ffffff" />
        {/* axes */}
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#e5e7eb" />
        <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#e5e7eb" />
        {/* path */}
        <path d={path} fill="none" stroke={color} strokeWidth={2} />
      </svg>
    </div>
  );
};

const formatDate = (d: Date) => d.toISOString().slice(0, 10);

const Reporting: React.FC = () => {
  const { selectedCompany } = useCompany();
  const { t } = useTranslation();
  const [from, setFrom] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return formatDate(d);
  });
  const [to, setTo] = useState<string>(() => formatDate(new Date()));
  const [sales, setSales] = useState<TimeSeriesPoint[]>([]);
  const [orders, setOrders] = useState<TimeSeriesPoint[]>([]);
  const [customers, setCustomers] = useState<TimeSeriesPoint[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, o, c] = await Promise.all([
        reportingAPI.sales({ from, to }),
        reportingAPI.orders({ from, to }),
        reportingAPI.customers({ from, to }),
      ]);
      setSales(s);
      setOrders(o);
      setCustomers(c);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCompany) {
      fetchAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  const handleApply = () => fetchAll();

  return (
    <div className="space-y-6">
      <div className="flex items-end gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 block w-48 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 block w-48 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm" />
        </div>
        <button onClick={handleApply} disabled={loading} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
          {loading ? 'Loading...' : 'Apply'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <LineChart title="Sales" series={sales} color="#16a34a" />
        <LineChart title="Orders" series={orders} color="#2563eb" />
        <LineChart title="Customers" series={customers} color="#9333ea" />
      </div>
    </div>
  );
};

export default Reporting;


